import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  CopyButton,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useParams } from 'react-router';
import type { ContractorDetail, RepresentativeAccountInvite } from '@isg/shared-types';
import { SOZLESME_DURUMU_ETIKETI } from '@isg/validation';
import {
  useLookup,
  usePersons,
  useTemsilciHesabiAc,
  useYuklenici,
  useYukleniciEkipmani,
  useYukleniciEvraklari,
  useYukleniciGuncelle,
  useYukleniciPersoneli,
} from '../api/queries';
import { gunGirdisiDegeri, gunGirdisiIso, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { EvrakTablosu, SahaGirisiNedenleri, SahaGirisiRozeti } from './evrak-parts';
import { EvrakPaketiBolumu } from './evrak-paketi-parts';
import { Bolum, KapiSebebi, KayitDetayi, Rozet } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * YÜKLENİCİ DETAY SAYFASI (SERTLEŞTİRME TUR 6 / Y2 — P5: bölümler alt alta,
 * sekme yok; P9: künye ızgarada). Keşif: firma + evrak + karar + dosya
 * yalnız API'de yaşıyordu; sekiz ucun web çağıranı yoktu.
 *
 * Bölümler: künye (Ş3) · saha girişi (Ş2 — rozet + NEDENLERİ yanında) ·
 * evraklar (yükleme + karar; yetkiler sunucudan) · personel (kişi düzeyi
 * saha girişi — K1) · ekipman · temsilci hesabı.
 */
export function YukleniciDetayScreen(): React.JSX.Element {
  const { contractorId } = useParams();
  const id = contractorId ?? '';
  const firma = useYuklenici(id);
  const evraklar = useYukleniciEvraklari(id);
  const personel = useYukleniciPersoneli(id);
  const ekipman = useYukleniciEkipmani(id);
  const evrakTipleri = useLookup('evrak_tipi');
  const [davetAcik, setDavetAcik] = useState(false);

  return (
    <Stack>
      <QueryStatus queries={[firma]} kayit />
      {firma.data && (
        <KayitDetayi
          testId="yuklenici-detay"
          geriYol="/yukleniciler"
          geriEtiket="Yüklenicilere dön"
          baslik={firma.data.ad}
          ustBilgi={
            <Group gap="xs">
              <SahaGirisiRozeti sahaGirisi={firma.data.sahaGirisi} />
              <Rozet anlam={firma.data.sozlesmeDurumu === 'bitmis' ? 'dikkat' : 'bilgi'}>
                {SOZLESME_DURUMU_ETIKETI[firma.data.sozlesmeDurumu]}
              </Rozet>
              {firma.data.degerlendirmeSkoru !== null && (
                <Badge variant="light">Skor: {firma.data.degerlendirmeSkoru}/100</Badge>
              )}
            </Group>
          }
          eylemler={
            firma.data.yetkiler.davetAcabilir ? (
              <Button
                size="xs"
                variant="light"
                data-testid="temsilci-hesabi-ac"
                onClick={() => setDavetAcik(true)}
              >
                Temsilci hesabı aç
              </Button>
            ) : undefined
          }
        >
          {davetAcik && (
            <RepresentativeInviteModal firma={firma.data} onClose={() => setDavetAcik(false)} />
          )}
          <KunyeBolumu firma={firma.data} />
          {/* K-102a…f — paket · paketten gelen · bu firmaya özel · yol planı. */}
          <EvrakPaketiBolumu firma={firma.data} />
          <Bolum baslik="Saha girişi" testId="saha-girisi-bolumu">
            <Group gap="sm" align="flex-start">
              <SahaGirisiRozeti
                sahaGirisi={firma.data.sahaGirisi}
                testId="saha-girisi-rozeti-bolum"
              />
              <SahaGirisiNedenleri sahaGirisi={firma.data.sahaGirisi} />
            </Group>
          </Bolum>
          <Bolum baslik="Evraklar" testId="evrak-bolumu">
            <QueryStatus queries={[evraklar, evrakTipleri]} />
            {evraklar.data && (
              <EvrakTablosu
                contractorId={id}
                documents={evraklar.data}
                evrakTipleri={evrakTipleri.data}
                personel={personel.data}
                yukleyebilir={firma.data.yetkiler.evrakKapisi.yazabilir}
              />
            )}
            <KapiSebebi
              durum={firma.data.yetkiler.evrakKapisi}
              nesne="Evrakı"
              fiil="yükler"
              testId="evrak-yazamama-sebebi"
            />
          </Bolum>
          <Bolum baslik="Personel" testId="personel-bolumu">
            <QueryStatus queries={[personel]} />
            {personel.data && personel.data.length === 0 && (
              <Text size="sm" c="dimmed">
                Firmanın aktif istihdamlı personeli yok.
              </Text>
            )}
            {personel.data && personel.data.length > 0 && (
              <Table data-testid="yuklenici-personel">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ad soyad</Table.Th>
                    <Table.Th>Görev</Table.Th>
                    <Table.Th>Saha</Table.Th>
                    <Table.Th>Saha girişi (kişi)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {personel.data.map((kisi) => (
                    <Table.Tr key={kisi.personId} data-testid="yuklenici-personel-satiri">
                      <Table.Td>{kisi.adSoyad}</Table.Td>
                      <Table.Td>{kisi.rol}</Table.Td>
                      <Table.Td>{kisi.workplaceAd}</Table.Td>
                      <Table.Td>
                        <SahaGirisiRozeti sahaGirisi={kisi.sahaGirisi} testId="kisi-saha-girisi" />
                        <SahaGirisiNedenleri sahaGirisi={kisi.sahaGirisi} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Bolum>
          <Bolum baslik="Ekipman" testId="ekipman-bolumu">
            <QueryStatus queries={[ekipman]} />
            {ekipman.data && ekipman.data.length === 0 && (
              <Text size="sm" c="dimmed">
                Firmaya ait ekipman kaydı yok.
              </Text>
            )}
            {ekipman.data && ekipman.data.length > 0 && (
              <Table data-testid="yuklenici-ekipman">
                <Table.Tbody>
                  {ekipman.data.map((e) => (
                    <Table.Tr key={e.id}>
                      <Table.Td>{e.ad}</Table.Td>
                      <Table.Td>{e.tipAd}</Table.Td>
                      <Table.Td>{e.sonrakiKontrol ? tarihGoster(e.sonrakiKontrol) : '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Bolum>
        </KayitDetayi>
      )}
    </Stack>
  );
}

/** KÜNYE — P9 ızgarası + tek "Kaydet"; düğme kapsamını söyler (madde 140). */
function KunyeBolumu({ firma }: { firma: ContractorDetail }): React.JSX.Element {
  const guncelle = useYukleniciGuncelle();
  const kisiler = usePersons('aktif');
  const [taslak, setTaslak] = useState({
    ad: firma.ad,
    vergiNo: firma.vergiNo ?? '',
    yetkiliAd: firma.yetkiliAd ?? '',
    yetkiliTel: firma.yetkiliTel ?? '',
    yetkiliEposta: firma.yetkiliEposta ?? '',
    sozlesmeBaslangic: gunGirdisiDegeri(firma.sozlesmeBaslangic),
    sozlesmeBitis: gunGirdisiDegeri(firma.sozlesmeBitis),
    sorumluPersonId: firma.sorumluPersonId,
  });
  useEffect(() => {
    setTaslak({
      ad: firma.ad,
      vergiNo: firma.vergiNo ?? '',
      yetkiliAd: firma.yetkiliAd ?? '',
      yetkiliTel: firma.yetkiliTel ?? '',
      yetkiliEposta: firma.yetkiliEposta ?? '',
      sozlesmeBaslangic: gunGirdisiDegeri(firma.sozlesmeBaslangic),
      sozlesmeBitis: gunGirdisiDegeri(firma.sozlesmeBitis),
      sorumluPersonId: firma.sorumluPersonId,
    });
  }, [firma]);
  const yazabilir = firma.yetkiler.kunyeYazabilir;
  const baslangicIso = taslak.sozlesmeBaslangic ? gunGirdisiIso(taslak.sozlesmeBaslangic) : null;
  const bitisIso = taslak.sozlesmeBitis ? gunGirdisiIso(taslak.sozlesmeBitis) : null;
  const tarihlerGecerli =
    (taslak.sozlesmeBaslangic === '' || baslangicIso !== null) &&
    (taslak.sozlesmeBitis === '' || bitisIso !== null);
  const degisti =
    taslak.ad !== firma.ad ||
    taslak.vergiNo !== (firma.vergiNo ?? '') ||
    taslak.yetkiliAd !== (firma.yetkiliAd ?? '') ||
    taslak.yetkiliTel !== (firma.yetkiliTel ?? '') ||
    taslak.yetkiliEposta !== (firma.yetkiliEposta ?? '') ||
    (baslangicIso ? taslak.sozlesmeBaslangic : null) !== firma.sozlesmeBaslangic ||
    (bitisIso ? taslak.sozlesmeBitis : null) !== firma.sozlesmeBitis ||
    taslak.sorumluPersonId !== firma.sorumluPersonId;
  const alan = (
    anahtar: keyof typeof taslak,
    etiket: string,
    testId: string,
    tip?: 'date',
  ): React.JSX.Element => (
    <TextInput
      label={etiket}
      type={tip}
      data-testid={testId}
      value={String(taslak[anahtar] ?? '')}
      disabled={!yazabilir}
      onChange={(e) => setTaslak({ ...taslak, [anahtar]: e.currentTarget.value })}
    />
  );
  return (
    <Bolum baslik="Künye" testId="kunye-bolumu">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {alan('ad', 'Firma adı', 'kunye-ad')}
        {alan('vergiNo', 'Vergi no', 'kunye-vergi-no')}
        <Select
          label="Sorumlu kişi (bilgi)"
          data-testid="kunye-sorumlu"
          searchable
          clearable
          disabled={!yazabilir}
          comboboxProps={{ withinPortal: true }}
          data={(kisiler.data ?? []).map((k) => ({ value: k.id, label: k.adSoyad }))}
          value={taslak.sorumluPersonId}
          onChange={(deger) => setTaslak({ ...taslak, sorumluPersonId: deger })}
        />
        {alan('yetkiliAd', 'Yetkili', 'kunye-yetkili-ad')}
        {alan('yetkiliTel', 'Yetkili telefon', 'kunye-yetkili-tel')}
        {alan('yetkiliEposta', 'Yetkili e-posta', 'kunye-yetkili-eposta')}
        {alan('sozlesmeBaslangic', 'Sözleşme başlangıcı', 'kunye-sozlesme-baslangic', 'date')}
        {alan(
          'sozlesmeBitis',
          'Sözleşme bitişi (boş = sabit alt işveren)',
          'kunye-sozlesme-bitis',
          'date',
        )}
      </SimpleGrid>
      {yazabilir && (
        <Group justify="flex-end" mt="sm" gap="xs">
          {degisti && (
            <Rozet anlam="dikkat" testId="kunye-kaydedilmedi">
              Kaydedilmemiş değişiklik
            </Rozet>
          )}
          {guncelle.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="kunye-hata">
              {guncelle.error.message}
            </Text>
          )}
          <Button
            size="xs"
            data-testid="kunye-kaydet"
            disabled={!degisti || !tarihlerGecerli}
            loading={guncelle.isPending}
            onClick={() =>
              guncelle.mutate({
                id: firma.id,
                input: {
                  ad: taslak.ad.trim(),
                  vergiNo: taslak.vergiNo.trim() || null,
                  yetkiliAd: taslak.yetkiliAd.trim() || null,
                  yetkiliTel: taslak.yetkiliTel.trim() || null,
                  yetkiliEposta: taslak.yetkiliEposta.trim() || null,
                  sozlesmeBaslangic: baslangicIso ? taslak.sozlesmeBaslangic : null,
                  sozlesmeBitis: bitisIso ? taslak.sozlesmeBitis : null,
                  sorumluPersonId: taslak.sorumluPersonId,
                },
              })
            }
          >
            Künyeyi kaydet
          </Button>
        </Group>
      )}
    </Bolum>
  );
}

/**
 * Davet modalı (F1.5·S11 — listeden detaya taşındı): geçici parola YALNIZ bu
 * görünümde yaşar; e-posta seçeneği F2·S1.
 */
function RepresentativeInviteModal({
  firma,
  onClose,
}: {
  firma: ContractorDetail;
  onClose: () => void;
}): React.JSX.Element {
  const davet = useTemsilciHesabiAc();
  const [email, setEmail] = useState('');
  const [ad, setAd] = useState('');
  const [soyad, setSoyad] = useState('');
  const [epostaylaGonder, setEpostaylaGonder] = useState(true);

  return (
    <Modal opened onClose={onClose} title={`Temsilci hesabı — ${firma.ad}`} size="lg">
      {davet.data ? (
        <InvitePackage paket={davet.data} />
      ) : (
        <Stack>
          <TextInput
            label="E-posta (kullanıcı adı olur)"
            data-testid="davet-email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <TextInput
            label="Ad"
            data-testid="davet-ad"
            value={ad}
            onChange={(e) => setAd(e.currentTarget.value)}
          />
          <TextInput
            label="Soyad"
            data-testid="davet-soyad"
            value={soyad}
            onChange={(e) => setSoyad(e.currentTarget.value)}
          />
          <Checkbox
            label="Daveti e-postayla da gönder"
            data-testid="davet-eposta-gonder"
            checked={epostaylaGonder}
            onChange={(e) => setEpostaylaGonder(e.currentTarget.checked)}
          />
          {davet.isError && (
            <Alert color={ANLAM.engel} role="alert">
              {davet.error.message}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button
              data-testid="davet-gonder"
              loading={davet.isPending}
              onClick={() =>
                davet.mutate({
                  contractorId: firma.id,
                  input: { email, ad, soyad, epostaylaGonder },
                })
              }
            >
              Hesabı aç
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function InvitePackage({ paket }: { paket: RepresentativeAccountInvite }): React.JSX.Element {
  return (
    <Stack>
      <Alert color={ANLAM.olumlu} title="Hesap açıldı">
        Geçici parola yalnız bu pencerede görünür — davet metnini şimdi kopyalayın.
      </Alert>
      {paket.epostaGonderildi === true && (
        <Alert color={ANLAM.olumlu} data-testid="davet-eposta-sonuc">
          Davet e-postası temsilcinin adresine gönderildi.
        </Alert>
      )}
      {paket.epostaGonderildi === false && (
        <Alert color={ANLAM.dikkat} data-testid="davet-eposta-sonuc" role="alert">
          Davet e-postası gönderilemedi — metni kopyalayıp temsilciye kendi kanalınızla iletin.
        </Alert>
      )}
      <Text
        size="sm"
        style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
        data-testid="davet-metni"
      >
        {paket.davetMetni}
      </Text>
      <CopyButton value={paket.davetMetni}>
        {({ copied, copy }) => (
          <Button
            data-testid="davet-kopyala"
            color={copied ? ANLAM.olumlu : undefined}
            onClick={copy}
          >
            {copied ? 'Kopyalandı' : 'Davet metnini kopyala'}
          </Button>
        )}
      </CopyButton>
    </Stack>
  );
}
